import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Sun, Cloud, Palette, Image, ChevronDown, Check } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';

/**
 * MODERNE SLIDE-UP PICKER für V6 Einstellungen
 * Luma/Instagram-inspired, nur visuell geändert, Logik gleich!
 */
export default function V6ModernPicker({ 
  timeOfDay, setTimeOfDay,
  weather, setWeather,
  camera, setCamera,
  style, setStyle,
  imageFormat, setImageFormat,
  substyle, setSubstyle
}) {
  
  const [activePicker, setActivePicker] = useState(null);
  const [dynamicOptions, setDynamicOptions] = useState(null); // NEU: Dynamische Optionen
  const [loading, setLoading] = useState(true);
  
  // Lade Dropdown-Optionen aus Backend
  useEffect(() => {
    loadDropdownOptions();
  }, []);
  
  const loadDropdownOptions = async () => {
    try {
      const res = await apiClient.get('/admin/astria-tuning/public/dropdowns');
      if (res.ok) {
        const data = await res.json();
        setDynamicOptions(data);
      }
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Block body scroll when modal is open
  useEffect(() => {
    if (activePicker) {
      // Speichere aktuelle Scroll-Position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
    } else {
      // Stelle Scroll-Position wieder her
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [activePicker]);
  
  const getOptions = () => {
    if (dynamicOptions) {
      // Dynamische Optionen aus Admin-Panel
      return {
        timeOfDay: Object.entries(dynamicOptions.time_of_day || {}).map(([key, data]) => {
          const isOldFormat = typeof data === 'string';
          return {
            value: key,
            label: isOldFormat ? getGermanLabel('timeOfDay', key) : (data.label_de || key),
            icon: getIconForKey('timeOfDay', key),
            desc: isOldFormat ? getGermanDesc('timeOfDay', key) : (data.desc_de || '')
          };
        }),
        weather: Object.entries(dynamicOptions.weather || {}).map(([key, data]) => {
          const isOldFormat = typeof data === 'string';
          return {
            value: key,
            label: isOldFormat ? getGermanLabel('weather', key) : (data.label_de || key),
            icon: getIconForKey('weather', key),
            desc: isOldFormat ? getGermanDesc('weather', key) : (data.desc_de || '')
          };
        }),
        camera: Object.entries(dynamicOptions.camera || {}).map(([key, data]) => {
          const isOldFormat = typeof data === 'string';
          return {
            value: key,
            label: isOldFormat ? getGermanLabel('camera', key) : (data.label_de || key),
            icon: getIconForKey('camera', key),
            desc: isOldFormat ? getGermanDesc('camera', key) : (data.desc_de || '')
          };
        }),
        style: Object.entries(dynamicOptions.styles || {}).map(([key, data]) => {
          const isOldFormat = typeof data === 'string';
          return {
            value: key,
            label: isOldFormat ? getGermanLabel('style', key) : (data.label_de || key),
            icon: '🎨',
            desc: isOldFormat ? getGermanDesc('style', key) : (data.desc_de || '')
          };
        }),
        imageFormat: Object.entries(dynamicOptions.formats || {}).map(([key, data]) => ({
          value: key,
          label: data.label_de || key,
          icon: '🖼️',
          desc: data.desc_de || ''
        }))
      };
    }
    
    // Fallback: Hardcoded options (falls Backend nicht erreichbar)
    return staticOptions;
  };
  
  const getGermanLabel = (type, key) => {
    const labelMap = {
      timeOfDay: {
        day: 'Tag', sunrise: 'Sonnenaufgang', sunset: 'Sonnenuntergang',
        golden_hour: 'Golden Hour', night: 'Nacht', blue_hour: 'Blaue Stunde'
      },
      weather: {
        clear: 'Klar', cloudy: 'Bewölkt', rain: 'Regen', storm: 'Gewitter',
        snow: 'Schnee', windy: 'Wind', fog: 'Nebel', dust: 'Staub'
      },
      camera: {
        '24mm': '24mm Weitwinkel', '35mm': '35mm Standard', '50mm': '50mm Neutral',
        '85mm': '85mm Portrait', drone: 'Drohne', top: 'Von oben', low: 'Von unten'
      },
      style: {
        cinematic: 'Filmisch', cinematic_epic: 'Filmisch - Episch', 
        cinematic_noir: 'Filmisch - Noir', cinematic_vintage: 'Filmisch - Vintage',
        cinematic_moody: 'Filmisch - Moody', fashion: 'Fashion',
        fashion_glamour: 'Fashion - Glamour', fashion_editorial: 'Fashion - Editorial',
        fashion_vintage: 'Fashion - Vintage', realistic: 'Realistisch',
        realistic_natural: 'Realistisch - Natürlich', realistic_candid: 'Realistisch - Candid',
        action: 'Action', action_standard: 'Action', documentary: 'Dokumentarisch',
        documentary_standard: 'Dokumentarisch', street: 'Street', street_standard: 'Street',
        nature: 'Natur', nature_standard: 'Natur'
      }
    };
    return labelMap[type]?.[key] || key;
  };
  
  const getGermanDesc = (type, key) => {
    const descMap = {
      timeOfDay: {
        day: 'helles Tageslicht', sunrise: 'goldenes Morgenlicht', sunset: 'warmes Abendlicht',
        golden_hour: 'magisches Licht', night: 'künstliches Licht', blue_hour: 'Dämmerung'
      },
      weather: {
        clear: 'strahlend, wolkenlos', cloudy: 'diffuses Licht', rain: 'nass, Wassertropfen',
        storm: 'dramatisch, Blitze', snow: 'winterlich, weiß', windy: 'dynamisch, Bewegung',
        fog: 'mystisch, verhüllt', dust: 'sandig, wüstenartig'
      },
      camera: {
        '24mm': 'Große Szenen', '35mm': 'Ausgewogen', '50mm': 'Natürlich',
        '85mm': 'Nahaufnahme', drone: 'Vogelperspektive', top: 'Aufsicht', low: 'Heroisch'
      },
      style: {
        cinematic: 'Großes Kino', cinematic_epic: 'Episch', cinematic_noir: 'Drama, Schatten',
        cinematic_vintage: 'Retro-Film', cinematic_moody: 'Düster', fashion: 'Mode',
        fashion_glamour: 'Hochglanz', fashion_editorial: 'Vogue-Style', fashion_vintage: 'Retro-Mode',
        realistic: 'Alltag', realistic_natural: 'Alltag', realistic_candid: 'Spontan',
        action: 'Dynamisch, Sport', documentary: 'Reportage', street: 'Urban', nature: 'Outdoor'
      }
    };
    return descMap[type]?.[key] || '';
  };
  
  const getIconForKey = (type, key) => {
    const iconMap = {
      timeOfDay: { day: '☀️', sunrise: '🌅', sunset: '🌇', golden_hour: '✨', night: '🌙', blue_hour: '🌆' },
      weather: { clear: '☀️', cloudy: '☁️', rain: '🌧️', storm: '⛈️', snow: '❄️', windy: '🌬️', fog: '🌫️', dust: '🏜️' },
      camera: { '24mm': '📐', '35mm': '🎥', '50mm': '📸', '85mm': '🎯', drone: '🚁', top: '👁️', low: '🦶' }
    };
    return iconMap[type]?.[key] || '🎨';
  };
  
  const staticImageFormats = [
    { value: 'portrait_9_16', label: '9:16 Hochkant', icon: '📱', desc: 'Instagram Story' },
    { value: 'landscape_16_9', label: '16:9 Quer', icon: '🖥️', desc: 'YouTube' },
    { value: 'square', label: '1:1 Quadrat', icon: '⬜', desc: 'Instagram Feed' }
  ];
  
  // Statische Fallback-Optionen (ALLE beibehalten!)
  const staticOptions = {
    timeOfDay: [
      { value: 'day', label: 'Tag', icon: '☀️', desc: 'helles Tageslicht' },
      { value: 'sunrise', label: 'Sonnenaufgang', icon: '🌅', desc: 'goldenes Morgenlicht' },
      { value: 'sunset', label: 'Sonnenuntergang', icon: '🌇', desc: 'warmes Abendlicht' },
      { value: 'golden_hour', label: 'Golden Hour', icon: '✨', desc: 'magisches Licht' },
      { value: 'night', label: 'Nacht', icon: '🌙', desc: 'künstliches Licht' },
      { value: 'blue_hour', label: 'Blaue Stunde', icon: '🌆', desc: 'Dämmerung' }
    ],
    weather: [
      { value: 'clear', label: 'Klar', icon: '☀️', desc: 'strahlend, wolkenlos' },
      { value: 'cloudy', label: 'Bewölkt', icon: '☁️', desc: 'diffuses Licht' },
      { value: 'rain', label: 'Regen', icon: '🌧️', desc: 'nass, Wassertropfen' },
      { value: 'storm', label: 'Gewitter', icon: '⛈️', desc: 'dramatisch, Blitze' },
      { value: 'snow', label: 'Schnee', icon: '❄️', desc: 'winterlich, weiß' },
      { value: 'windy', label: 'Wind', icon: '🌬️', desc: 'dynamisch, Bewegung' },
      { value: 'fog', label: 'Nebel', icon: '🌫️', desc: 'mystisch, verhüllt' },
      { value: 'dust', label: 'Staub', icon: '🏜️', desc: 'sandig, wüstenartig' }
    ],
    camera: [
      { value: '24mm', label: '24mm Weitwinkel', icon: '📐', desc: 'Große Szenen' },
      { value: '35mm', label: '35mm Standard', icon: '🎥', desc: 'Ausgewogen' },
      { value: '50mm', label: '50mm Neutral', icon: '📸', desc: 'Natürlich' },
      { value: '85mm', label: '85mm Portrait', icon: '🎯', desc: 'Nahaufnahme' },
      { value: 'drone', label: 'Drohne', icon: '🚁', desc: 'Vogelperspektive' },
      { value: 'top', label: 'Von oben', icon: '👁️', desc: 'Aufsicht' },
      { value: 'low', label: 'Von unten', icon: '🦶', desc: 'Heroisch' }
    ],
    imageFormat: [
      { value: 'portrait_9_16', label: '9:16 Hochkant', icon: '📱', desc: 'Instagram Story' },
      { value: 'landscape_16_9', label: '16:9 Quer', icon: '🖥️', desc: 'YouTube' },
      { value: 'square', label: '1:1 Quadrat', icon: '⬜', desc: 'Instagram Feed' }
    ],
    style: [
      { value: 'cinematic_epic', label: 'Filmisch - Episch', icon: '🎥', desc: 'Großes Kino' },
      { value: 'cinematic_noir', label: 'Filmisch - Noir', icon: '🎬', desc: 'Drama, Schatten' },
      { value: 'cinematic_vintage', label: 'Filmisch - Vintage', icon: '📼', desc: 'Retro-Film' },
      { value: 'cinematic_moody', label: 'Filmisch - Moody', icon: '🌑', desc: 'Düster' },
      { value: 'fashion_glamour', label: 'Fashion - Glamour', icon: '💎', desc: 'Hochglanz' },
      { value: 'fashion_editorial', label: 'Fashion - Editorial', icon: '📰', desc: 'Vogue-Style' },
      { value: 'fashion_vintage', label: 'Fashion - Vintage', icon: '📼', desc: 'Retro-Mode' },
      { value: 'realistic_natural', label: 'Realistisch - Natürlich', icon: '🌿', desc: 'Alltag' },
      { value: 'realistic_candid', label: 'Realistisch - Candid', icon: '📸', desc: 'Spontan' },
      { value: 'action_standard', label: 'Action', icon: '🔥', desc: 'Dynamisch, Sport' },
      { value: 'documentary_standard', label: 'Dokumentarisch', icon: '🎞️', desc: 'Reportage' },
      { value: 'street_standard', label: 'Street', icon: '🌃', desc: 'Urban' },
      { value: 'nature_standard', label: 'Natur', icon: '🏞️', desc: 'Outdoor' }
    ]
  };
  
  const options = getOptions(); // Dynamisch oder Fallback
  
  // Get current label
  const getCurrentLabel = (type, value) => {
    const opt = options[type].find(o => o.value === value);
    return opt ? opt.label : value;
  };
  
  // Handle selection - optimiert für schnelles Feedback
  const handleSelect = (type, value) => {
    console.log('✅ V6ModernPicker: Selected', type, value);
    
    // State-Updates
    if (type === 'timeOfDay') setTimeOfDay(value);
    else if (type === 'weather') setWeather(value);
    else if (type === 'camera') setCamera(value);
    else if (type === 'imageFormat') setImageFormat(value);
    else if (type === 'style') {
      setStyle(value);
    }
    
    // Schließe Modal und scroll zurück
    closePickerAndScrollBack();
  };
  
  const closePickerAndScrollBack = () => {
    setActivePicker(null);
    
    // Scroll zurück zum Textarea
    setTimeout(() => {
      const textarea = document.querySelector('[data-testid="dream-image-textarea"]');
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };
  
  // Picker Button Component - History & Vorlagen Style!
  const PickerButton = ({ icon: Icon, label, currentValue, onClick, 'data-testid': dataTestId }) => (
    <button
      onClick={() => {
        console.log('🖱️ Picker clicked:', label);
        onClick();
      }}
      className="w-full border-t-4 border-[#10B981]/40 rounded-xl overflow-hidden bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm hover:bg-white/10 transition-all duration-200 shadow-lg"
      data-testid={dataTestId}
    >
      <div className="px-4 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#10B981]" />
          <div className="text-left">
            <p className="text-xs text-white/70 font-medium">{label}</p>
            <p className="text-sm text-white font-semibold">{currentValue}</p>
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-[#10B981]" />
      </div>
    </button>
  );
  
  return (
    <>
      <div className="space-y-3">
        
        {/* Tageszeit Picker */}
        <PickerButton
          icon={Sun}
          label="Tageszeit"
          currentValue={getCurrentLabel('timeOfDay', timeOfDay)}
          onClick={() => setActivePicker('timeOfDay')}
          data-testid="picker-button-timeOfDay"
        />
        
        {/* Wetter Picker */}
        <PickerButton
          icon={Cloud}
          label="Wetter"
          currentValue={getCurrentLabel('weather', weather)}
          onClick={() => setActivePicker('weather')}
          data-testid="picker-button-weather"
        />
        
        {/* Kamera Picker */}
        <PickerButton
          icon={Camera}
          label="Kamera"
          currentValue={getCurrentLabel('camera', camera)}
          onClick={() => setActivePicker('camera')}
          data-testid="picker-button-camera"
        />
        
        {/* Bildstil Picker */}
        <PickerButton
          icon={Palette}
          label="Bildstil"
          currentValue={getCurrentLabel('style', `${style}_${substyle}`)}
          onClick={() => setActivePicker('style')}
          data-testid="picker-button-style"
        />
        
        {/* Format Picker */}
        <PickerButton
          icon={Image}
          label="Bildformat"
          currentValue={getCurrentLabel('imageFormat', imageFormat)}
          onClick={() => setActivePicker('imageFormat')}
          data-testid="picker-button-imageFormat"
        />
        
      </div>
      
      {/* SLIDE-UP PANEL - Als Portal für viewport-basierte Position */}
      {activePicker && createPortal(
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[999999] flex items-end overflow-hidden"
          onClick={closePickerAndScrollBack}
          data-testid="picker-panel-overlay"
          style={{ touchAction: 'none' }}
        >
          <div 
            className="bg-black backdrop-blur-3xl border-t-2 border-[#10B981]/40 rounded-t-3xl w-full max-h-[70vh] overflow-y-auto animate-slide-up shadow-2xl shadow-[#10B981]/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="picker-panel-content"
          >
            <div className="p-6">
              {/* Handle-Strich - GRÜNER LICHTSTRAHL */}
              <div className="w-14 h-1.5 bg-gradient-to-r from-transparent via-[#10B981] to-transparent rounded-full mx-auto mb-6 shadow-md shadow-[#10B981]/30"></div>
              
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">
                  {activePicker === 'timeOfDay' && 'Tageszeit wählen'}
                  {activePicker === 'weather' && 'Wetter wählen'}
                  {activePicker === 'camera' && 'Kamera wählen'}
                  {activePicker === 'style' && 'Bildstil wählen'}
                  {activePicker === 'imageFormat' && 'Format wählen'}
                </h3>
                <button 
                  onClick={closePickerAndScrollBack}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition-colors duration-150 shadow-sm"
                  data-testid="picker-panel-close-button"
                  aria-label="Picker schließen"
                >
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Options Grid */}
              <div className="space-y-3">
                {options[activePicker]?.map((opt) => {
                  const currentVal = activePicker === 'timeOfDay' ? timeOfDay :
                                    activePicker === 'weather' ? weather :
                                    activePicker === 'camera' ? camera :
                                    activePicker === 'imageFormat' ? imageFormat :
                                    `${style}_${substyle}`;
                  
                  const isSelected = opt.value === currentVal;
                  
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleSelect(activePicker, opt.value)}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 ${
                        isSelected 
                          ? 'bg-[#10B981]/20 border-[#10B981] shadow-lg shadow-[#10B981]/20' 
                          : 'bg-[#10B981]/5 border-[#10B981]/20 hover:bg-[#10B981]/10 hover:border-[#10B981]/40 hover:shadow-md'
                      }`}
                      data-testid={`picker-option-${opt.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className={`font-bold text-base ${isSelected ? 'text-white' : 'text-white/90'}`}>
                            {opt.label}
                          </p>
                          <p className={`text-xs mt-0.5 ${isSelected ? 'text-[#10B981]' : 'text-white/60'}`}>{opt.desc}</p>
                        </div>
                        {isSelected && (
                          <div className="w-7 h-7 rounded-full bg-[#10B981] flex items-center justify-center shadow-lg shadow-[#10B981]/40">
                            <Check className="w-4 h-4 text-black" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
